import { useState, useCallback } from 'react';
import { OTPInput } from '@/components/auth/OTPInput';
import { sendOTPForSettings, verifyOTPForSettings } from '@/services/settings';

interface CurrentEmailVerificationProps {
  currentEmail: string;
  userName: string;
  onVerified: () => void;
  onCancel: () => void;
}

export function CurrentEmailVerification({ 
  currentEmail, 
  userName, 
  onVerified, 
  onCancel
}: CurrentEmailVerificationProps) {
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>();
  const [nextAllowedAt, setNextAllowedAt] = useState<string | undefined>();

  const handleSendOTP = useCallback(async () => {
    setIsResending(true);
    setOtpError('');

    try {
      const result = await sendOTPForSettings(currentEmail, 'change_email', userName);
      
      if (result.success) {
        setShowOTPInput(true);
        setRemainingAttempts(result.data?.remainingAttempts);
      } else {
        setOtpError(result.error || '인증번호 발송에 실패했습니다.');
        setRemainingAttempts(result.data?.remainingAttempts);
        setNextAllowedAt(result.data?.nextAllowedAt);
      }
    } catch (error) {
      console.error('OTP send error:', error);
      setOtpError('인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setIsResending(false);
    }
  }, [currentEmail, userName]);

  const handleVerifyOTP = async (otp: string) => {
    setIsOTPLoading(true);
    setOtpError('');

    try {
      const result = await verifyOTPForSettings(currentEmail, otp, 'change_email');
      
      if (result.success) {
        onVerified();
      } else {
        setOtpError(result.error || '인증번호가 올바르지 않습니다.');
        setRemainingAttempts(result.data?.remainingAttempts);
      }
    } catch (error) {
      console.error('OTP verify error:', error);
      setOtpError('인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsOTPLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h4 className="text-base font-semibold text-gray-900 mb-2">
          현재 이메일 확인
        </h4>
        <p className="text-sm text-gray-600">
          현재 사용 중인 이메일 주소를 인증해주세요
        </p>
      </div>

      {/* 현재 이메일 표시 */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          현재 이메일
        </label>
        <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-center">
          {currentEmail}
        </div>
      </div>

      {!showOTPInput ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 text-center">
            위 이메일 주소로 인증번호를 발송해드립니다.
          </p>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={onCancel}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              취소
            </button>
            
            <button
              onClick={handleSendOTP}
              disabled={isResending}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                !isResending
                  ? 'bg-[#4CD964] text-white hover:bg-[#45B85C]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isResending ? '발송 중...' : '인증번호 받기'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-sm text-gray-600 mb-4 text-center">
            <strong>{currentEmail}</strong>로 발송된 인증번호를 입력해주세요
          </div>
          
          <OTPInput
            onComplete={handleVerifyOTP}
            onResend={handleSendOTP}
            isLoading={isOTPLoading}
            isResending={isResending}
            error={otpError}
            remainingAttempts={remainingAttempts}
            nextAllowedAt={nextAllowedAt}
          />
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={onCancel}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
}